import * as React from "react"
import { cn } from "@/lib/utils"
import { Input } from "./input"
import { Check } from "lucide-react"

const Autocomplete = React.forwardRef(({ 
  className, 
  options = [], 
  value, 
  onChange, 
  onSelect,
  placeholder,
  ...props 
}, ref) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState(value || '')
  const [filteredOptions, setFilteredOptions] = React.useState([])
  const wrapperRef = React.useRef(null)

  React.useEffect(() => {
    setInputValue(value || '')
  }, [value])

  React.useEffect(() => {
    if (inputValue.length > 0) {
      const filtered = options.filter(option => 
        option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
        (option.value && option.value.toLowerCase().includes(inputValue.toLowerCase()))
      ).slice(0, 10) // Limit to 10 results
      setFilteredOptions(filtered)
    } else {
      setFilteredOptions([])
    }
  }, [inputValue, options])

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleInputChange = (e) => {
    const newValue = e.target.value
    setInputValue(newValue)
    onChange?.(newValue)
    setIsOpen(true)
  }

  const handleSelect = (option) => {
    setInputValue(option.label)
    onSelect?.(option)
    onChange?.(option.label)
    setIsOpen(false)
  }

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        ref={ref}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => inputValue.length > 0 && setIsOpen(true)}
        placeholder={placeholder}
        className={cn("bg-white border-gray-300 text-gray-900", className)}
        autoComplete="off"
        {...props}
      />
      {isOpen && filteredOptions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {filteredOptions.map((option, index) => (
            <div
              key={option.value || index}
              onClick={() => handleSelect(option)}
              className={cn(
                "px-3 py-2 cursor-pointer text-sm text-gray-900 hover:bg-blue-50 flex items-center justify-between",
                inputValue.toLowerCase() === option.label.toLowerCase() && "bg-blue-50"
              )}
            >
              <span>{option.label}</span>
              {inputValue.toLowerCase() === option.label.toLowerCase() && (
                <Check className="w-4 h-4 text-blue-600" />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
})

Autocomplete.displayName = "Autocomplete"

export { Autocomplete }
